import React, { useState } from 'react';
import { Box, Container, Heading, Text, VStack, useColorModeValue, Divider, FormControl, FormLabel, Input, Textarea, Button, SimpleGrid, Flex, Icon, Link, HStack, FormErrorMessage, useToast } from '@chakra-ui/react';
import { Mail, Linkedin, Github, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
const MotionBox = motion(Box);
const ContactInfo = ({
  icon,
  title,
  content,
  link
}) => {
  return <HStack spacing={4} data-id="element-94">
      <Flex w={12} h={12} align="center" justify="center" borderRadius="full" bg="blue.100" color="blue.600" data-id="element-95">
        <Icon as={icon} boxSize={5} data-id="element-96" />
      </Flex>
      <VStack align="start" spacing={0} data-id="element-97">
        <Text fontWeight="bold" data-id="element-98">{title}</Text>
        {link ? <Link href={link} color="blue.500" isExternal data-id="element-99">
            {content}
          </Link> : <Text color={useColorModeValue('gray.600', 'gray.300')} data-id="element-100">
            {content}
          </Text>}
      </VStack>
    </HStack>;
};
export function Contact() {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    return newErrors;
  };
  const handleSubmit = e => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      toast({
        title: 'Message sent!',
        description: "Thank you for your message. I'll get back to you soon.",
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    }, 1500);
  };
  return <Box id="contact" py={20} bg={bgColor} width="100%" data-id="element-101">
      <Container maxW="1200px" data-id="element-102">
        <VStack spacing={12} data-id="element-103">
          <VStack spacing={4} textAlign="center" maxW="800px" data-id="element-104">
            <MotionBox initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-105">
              <Heading as="h2" size="xl" data-id="element-106">
                Get In Touch
              </Heading>
            </MotionBox>
            <Divider width="100px" borderColor="blue.500" borderWidth={2} data-id="element-107" />
            <Text fontSize="lg" color={useColorModeValue('gray.600', 'gray.300')} data-id="element-108">
              Interested in working together? Feel free to contact me for any
              project or collaboration.
            </Text>
          </VStack>
          <SimpleGrid columns={{
          base: 1,
          md: 2
        }} spacing={10} width="100%" data-id="element-109">
            <MotionBox initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-110">
              <VStack spacing={8} align="start" p={{
              base: 6,
              md: 8
            }} bg={cardBg} borderRadius="lg" boxShadow="md" border="1px" borderColor={borderColor} height="100%" data-id="element-111">
                <Heading as="h3" size="md" data-id="element-112">
                  Contact Information
                </Heading>
                <VStack spacing={6} align="start" width="100%" data-id="element-113">
                  <ContactInfo icon={Mail} title="Email" content="george@grayspacegh.com" link="mailto:george@grayspacegh.com" data-id="element-114" />
                  <ContactInfo icon={Linkedin} title="LinkedIn" content="linkedin.com/in/georgeklenam" link="https://linkedin.com/in/georgeklenam" data-id="element-115" />
                  <ContactInfo icon={Github} title="GitHub" content="github.com/georgeklenam" link="https://github.com/georgeklenam" data-id="element-116" />
                  <ContactInfo icon={Phone} title="WhatsApp" content="+233 59 417 1887" link="https://wa.me/233594171887" data-id="element-117" />
                </VStack>
              </VStack>
            </MotionBox>
            <MotionBox initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-118">
              <VStack as="form" onSubmit={handleSubmit} spacing={6} p={{
              base: 6,
              md: 8
            }} bg={cardBg} borderRadius="lg" boxShadow="md" border="1px" borderColor={borderColor} data-id="element-119">
                <Heading as="h3" size="md" alignSelf="flex-start" data-id="element-120">
                  Send Me a Message
                </Heading>
                <FormControl isRequired isInvalid={errors.name} data-id="element-121">
                  <FormLabel data-id="element-122">Name</FormLabel>
                  <Input name="name" type="text" placeholder="Your name" focusBorderColor="blue.500" value={formData.name} onChange={handleChange} data-id="element-123" />
                  <FormErrorMessage data-id="element-124">{errors.name}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={errors.email} data-id="element-125">
                  <FormLabel data-id="element-126">Email</FormLabel>
                  <Input name="email" type="email" placeholder="Your email" focusBorderColor="blue.500" value={formData.email} onChange={handleChange} data-id="element-127" />
                  <FormErrorMessage data-id="element-128">{errors.email}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={errors.subject} data-id="element-129">
                  <FormLabel data-id="element-130">Subject</FormLabel>
                  <Input name="subject" type="text" placeholder="Subject" focusBorderColor="blue.500" value={formData.subject} onChange={handleChange} data-id="element-131" />
                  <FormErrorMessage data-id="element-132">{errors.subject}</FormErrorMessage>
                </FormControl>
                <FormControl isRequired isInvalid={errors.message} data-id="element-133">
                  <FormLabel data-id="element-134">Message</FormLabel>
                  <Textarea name="message" placeholder="Your message" rows={5} focusBorderColor="blue.500" value={formData.message} onChange={handleChange} data-id="element-135" />
                  <FormErrorMessage data-id="element-136">{errors.message}</FormErrorMessage>
                </FormControl>
                <Button colorScheme="blue" size="lg" width="100%" type="submit" isLoading={isSubmitting} bgGradient="linear(to-r, blue.400, blue.600)" _hover={{
                bgGradient: 'linear(to-r, blue.500, blue.700)'
              }} data-id="element-137">
                  Send Message
                </Button>
              </VStack>
            </MotionBox>
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>;
}